import { useState } from 'react';
import Modal from '../ui/Modal';
import type { BenchmarkRun, BenchmarkDocument } from '../../types/benchmark';
import { benchmarkService } from '../../services/benchmarkService';
import { TagsFilter } from './TagsFilter';
import { FlagBadgeList } from '../FlagBadge';

interface RunHistoryOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  runs: BenchmarkRun[];
  selectedRunId: string | null;
  onSelectRun: (runId: string) => void;
  onViewDocument?: (docId: number) => void;
  onDeleteRun?: (runId: string) => void;
  onLockRun?: (runId: string, locked: boolean) => void;
  onCancelRun?: (runId: string) => void;
  onDownloadLogs?: (runId: string) => void;
}

export function RunHistoryOverlay({
  isOpen,
  onClose,
  runs,
  selectedRunId,
  onSelectRun,
  onViewDocument,
  onDeleteRun,
  onLockRun,
  onCancelRun,
  onDownloadLogs,
}: RunHistoryOverlayProps) {
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [documentsCache, setDocumentsCache] = useState<Record<string, BenchmarkDocument[]>>({});
  const [loadingDocs, setLoadingDocs] = useState<Record<string, boolean>>({});
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const filteredRuns = selectedTags.length > 0
    ? runs.filter(run => selectedTags.some(tag => run.tags.includes(tag)))
    : runs;

  // Show all runs, sorted by creation date (newest first)
  const sortedRuns = [...filteredRuns].sort((a, b) =>
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const handleToggleExpand = async (runId: string) => {
    if (expandedRunId === runId) {
      setExpandedRunId(null);
      return;
    }

    setExpandedRunId(runId);

    // Only load documents for completed runs
    const run = runs.find(r => r.id === runId);
    if (run && run.status === 'completed' && !documentsCache[runId]) {
      setLoadingDocs({ ...loadingDocs, [runId]: true });
      try {
        const docs = await benchmarkService.getDocuments(runId);
        setDocumentsCache({ ...documentsCache, [runId]: docs });
      } catch (err) {
        console.error('Failed to load documents:', err);
      } finally {
        setLoadingDocs({ ...loadingDocs, [runId]: false });
      }
    }
  };

  const handleSelectRun = (runId: string) => {
    onSelectRun(runId);
    onClose();
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      completed: <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Terminé</span>,
      running: <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">En cours</span>,
      failed: <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">Échoué</span>,
      pending: <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">En attente</span>,
      cancelled: <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">Annulé</span>,
    };
    return badges[status as keyof typeof badges] || null;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Historique des runs" size="lg">
      {/* Tags Filter */}
      <div className="mb-4 pb-4 border-b border-gray-200">
        <TagsFilter
          runs={runs}
          selectedTags={selectedTags}
          onTagsChange={setSelectedTags}
        />
      </div>

      <div className="space-y-2">
        {sortedRuns.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Aucun run disponible
          </div>
        ) : (
          sortedRuns.map((run) => (
            <div
              key={run.id}
              className={`border rounded-lg ${
                selectedRunId === run.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
              }`}
            >
              {/* Run header - expandable */}
              <div className="p-4 cursor-pointer hover:bg-gray-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0" onClick={() => handleToggleExpand(run.id)}>
                    {/* Expand chevron */}
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 mt-0.5 ${
                        expandedRunId === run.id ? 'transform rotate-90' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>

                    {/* Run info - first line only */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 text-sm">
                        <span className="font-medium">
                          {run.is_locked && '🔒 '}
                          {run.id.slice(0, 8)}
                        </span>
                        <span className="text-gray-600">{run.domain}</span>
                        <span className="text-gray-600">
                          {run.total_documents} docs
                        </span>
                        <span className="font-medium text-blue-600">
                          F1: {run.avg_f1 !== null && run.avg_f1 !== undefined
                            ? Number(run.avg_f1).toFixed(3)
                            : 'N/A'}
                        </span>
                        {getStatusBadge(run.status)}
                      </div>
                    </div>
                  </div>

                  {/* Actions - aligned with first line */}
                  <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    {/* Lock/Unlock */}
                    {onLockRun && (
                      <button
                        onClick={() => onLockRun(run.id, !run.is_locked)}
                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50"
                        title={run.is_locked ? 'Déverrouiller' : 'Verrouiller'}
                      >
                        {run.is_locked ? '🔓' : '🔒'}
                      </button>
                    )}

                    {/* Download Logs */}
                    {onDownloadLogs && (
                      <button
                        onClick={() => run.status === 'completed' && onDownloadLogs(run.id)}
                        disabled={run.status !== 'completed'}
                        className={`px-3 py-1 text-sm border rounded ${
                          run.status === 'completed'
                            ? 'text-blue-600 hover:text-blue-800 border-blue-300 hover:bg-blue-50 cursor-pointer'
                            : 'text-gray-300 border-gray-200 cursor-not-allowed'
                        }`}
                        title={run.status === 'completed' ? 'Télécharger les logs' : 'Disponible seulement pour les runs terminées'}
                      >
                        📥
                      </button>
                    )}

                    {/* Cancel (for running runs) */}
                    {onCancelRun && (
                      <button
                        onClick={() => (run.status === 'running' || run.status === 'pending') && onCancelRun(run.id)}
                        disabled={run.status !== 'running' && run.status !== 'pending'}
                        className={`px-3 py-1 text-sm border rounded ${
                          run.status === 'running' || run.status === 'pending'
                            ? 'text-orange-600 hover:text-orange-800 border-orange-300 hover:bg-orange-50 cursor-pointer'
                            : 'text-gray-300 border-gray-200 cursor-not-allowed'
                        }`}
                        title={run.status === 'running' || run.status === 'pending' ? 'Arrêter' : 'Disponible seulement pour les runs en cours'}
                      >
                        ⏹
                      </button>
                    )}

                    {/* Delete */}
                    {onDeleteRun && (
                      <button
                        onClick={() => !run.is_locked && onDeleteRun(run.id)}
                        disabled={run.is_locked}
                        className={`px-3 py-1 text-sm border rounded ${
                          !run.is_locked
                            ? 'text-red-600 hover:text-red-800 border-red-300 hover:bg-red-50 cursor-pointer'
                            : 'text-gray-300 border-gray-200 cursor-not-allowed'
                        }`}
                        title={run.is_locked ? 'Impossible de supprimer une run verrouillée' : 'Supprimer'}
                      >
                        🗑
                      </button>
                    )}

                    {/* Select button */}
                    <button
                      onClick={() => handleSelectRun(run.id)}
                      className={`px-4 py-1 rounded text-sm font-medium ${
                        selectedRunId === run.id
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {selectedRunId === run.id ? '✓ Sélectionné' : 'Sélectionner'}
                    </button>
                  </div>
                </div>

                {/* Second line: note, timestamp (left) and versions (right) */}
                <div className="flex items-center justify-between text-xs text-gray-500 mt-2 px-4 pb-2">
                  <div className="flex items-center gap-3">
                    {run.note && (
                      <span className="truncate max-w-xs" title={run.note}>
                        📝 {run.note}
                      </span>
                    )}
                    <span className="text-gray-400 whitespace-nowrap">
                      {new Date(run.created_at).toLocaleString('fr-CA', {
                        timeZone: 'America/Montreal',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-400 whitespace-nowrap">
                    <span>Schéma: v{run.schema?.version || '-'}</span>
                    <span>|</span>
                    <span>Prompt: v{run.prompt?.version || run.prompt_version || '-'}</span>
                    <span>|</span>
                    <span>LLM: v{run.llm_config?.version || '-'}</span>
                    <span>|</span>
                    <span>OCR: v{run.ocr_config?.version || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Expanded content - progress bar or documents list */}
              {expandedRunId === run.id && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  {(run.status === 'running' || run.status === 'pending') ? (
                    <div className="py-4">
                      <div className="flex items-center justify-between mb-2 text-sm">
                        <span className="text-gray-700">
                          {run.status === 'pending' ? 'En attente de démarrage...' : 'Traitement en cours...'}
                        </span>
                        <span className="text-gray-600">
                          {run.processed_documents} / {run.total_documents} documents
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-primary h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${run.total_documents > 0 ? (run.processed_documents / run.total_documents) * 100 : 0}%` }}
                        ></div>
                      </div>
                      {run.current_document && (
                        <p className="text-xs text-gray-500 mt-2">
                          Document actuel: {run.current_document}
                        </p>
                      )}
                    </div>
                  ) : loadingDocs[run.id] ? (
                    <div className="text-center py-4 text-gray-500">Chargement des documents...</div>
                  ) : documentsCache[run.id] && documentsCache[run.id].length > 0 ? (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Documents ({documentsCache[run.id].length})
                      </h4>
                      <div className="max-h-60 overflow-y-auto space-y-1">
                        {documentsCache[run.id].map((doc) => (
                          <div
                            key={doc.id}
                            onClick={() => {
                              if (onViewDocument) {
                                // First select the run, then view the document
                                if (selectedRunId !== run.id) {
                                  onSelectRun(run.id);
                                }
                                onViewDocument(doc.id);
                              }
                            }}
                            className={`flex items-center justify-between text-sm p-2 bg-white rounded border border-gray-200 ${
                              onViewDocument ? 'cursor-pointer hover:bg-blue-50 hover:border-blue-300' : ''
                            }`}
                            title={onViewDocument ? 'Cliquer pour voir les détails' : ''}
                          >
                            <span className="text-gray-900 flex items-center gap-2 flex-wrap">
                              📄 {doc.filename}
                              {(doc.tags || []).includes('cas_complexe') && (
                                <span
                                  className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800 border border-amber-200"
                                  title="Document tagué cas_complexe : séquestre/faillite, multi-immeubles, structure juridique atypique"
                                >
                                  Complexe
                                </span>
                              )}
                              {/* PROD1.7.8 N2 — flags détectés par le LLM */}
                              <FlagBadgeList flags={doc.flags || []} compact />
                            </span>
                            <div className="flex items-center gap-3">
                              <span className="text-gray-600">
                                F1: {doc.f1 !== null && doc.f1 !== undefined ? doc.f1.toFixed(3) : 'N/A'}
                              </span>
                              {doc.has_critical_error && (
                                <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">
                                  Critique
                                </span>
                              )}
                              {!doc.has_critical_error && !doc.is_perfect && doc.error_count > 0 && (
                                <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800">
                                  {doc.error_count} erreur{doc.error_count > 1 ? 's' : ''}
                                </span>
                              )}
                              {doc.is_perfect && (
                                <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                                  ✓ Parfait
                                </span>
                              )}
                              <span className="text-gray-500">
                                {doc.processing_time_ms ? (doc.processing_time_ms / 1000).toFixed(1) : '?'}s
                              </span>
                              {onViewDocument && (
                                <span className="text-blue-600 text-xs">
                                  👁 Voir
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">Aucun document disponible</div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}
